import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Button, Space, message, Input, Select, Spin, Popconfirm } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import Heading from "../../components/Heading";
import "./style.scss";
import { TableComponent } from "../Master/CommonComponents";
import search_icon from "../../assests/images/search-icon.svg";
import { getPremiseId } from "../../utils/worker";
import API from "../../api";
import urls from "../../api/urls";
import Smcs_Loader from "../../assests/images/loader-new.gif";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  STATUS_OPTIONS,
  DEFAULT_HUB_ID,
  MESSAGE_TOP_OFFSET,
  DEFAULT_PAGE_SIZE,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  STATUS_COLORS,
  approveButtonStyle,
  rejectButtonStyle,
} from "./constants";

dayjs.extend(customParseFormat);

const mapRtoItem = (it) => ({
  awbNumber: it?.awbNumber ?? "",
  cpId: String(it?.cpId ?? ""),
  cpName: it?.cpName ?? "",
  status: it?.requestedStatus ?? "",
  rtoReason: it?.rtoReason ?? "",
  remarks: it?.remarks ?? "",
  requestedDate: it?.requestedDate ?? undefined,
});

const getUserInfo = () => {
  try {
    return JSON.parse(localStorage.getItem("userinfo")) || {};
  } catch (e) {
    console.error("Failed to parse userinfo from localStorage", e);
    return {};
  }
};

const ApprovalsRTORequest = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approvedAwbs, setApprovedAwbs] = useState(() => new Set());
  const [rejectedAwbs, setRejectedAwbs] = useState(() => new Set());

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });

  // Filters (declare before fetchData to avoid TDZ)
  const [awbQuery, setAwbQuery] = useState("");
  const [cpIdQuery, setCpIdQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("requested");

  useEffect(() => {
    message.config({ top: MESSAGE_TOP_OFFSET });
  }, []);

  const toTitleCase = (value) => {
    const str = (value ?? "").toString();
    if (!str) return "";
    return str
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
      .replace(/\b([a-z])/g, (m, p1) => p1.toUpperCase());
  };

  const { current: currentPage, pageSize } = pagination;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const bearerPid = getPremiseId(); // e.g., "Bearer <id>"
      const hubId =
        typeof bearerPid === "string" && bearerPid.startsWith("Bearer")
          ? bearerPid.split(" ")[1]
          : bearerPid || DEFAULT_HUB_ID;

      const params = new URLSearchParams();
      params.set("hubId", String(hubId));
      const cpIdTrim = (cpIdQuery || "").trim();
      if (cpIdTrim) params.set("cpId", cpIdTrim);
      const awbTrim = (awbQuery || "").trim();
      if (awbTrim) params.set("awbNumber", awbTrim);
      if (statusFilter) params.set("requestStatus", statusFilter);
      params.set("page", String(currentPage || 1));
      params.set("limit", String(pageSize || DEFAULT_PAGE_SIZE));

      const endpoint = `${urls.RTO_REQUEST_HUB}?${params.toString()}`;
      const resp = await API.Api(endpoint).get();
      if (!resp?.status) throw new Error(resp?.response?.data?.message || "Request failed");

      const json = resp.response?.data;
      const list = Array.isArray(json?.data) ? json.data.map(mapRtoItem) : [];

      setRows(list);
      setPagination((prev) => ({
        ...prev,
        total: json?.pagination?.totalCount ?? list.length,
      }));
    } catch (e) {
      console.error(e);
      message.error(ERROR_MESSAGES.FETCH_FAILED);
    } finally {
      setLoading(false);
    }
  }, [awbQuery, cpIdQuery, statusFilter, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateRtoRequestStatus = async (awbNumber, nextStatus) => {
    try {
      setLoading(true);
      const user = getUserInfo();
      const approvedBy = user?.username || user?.email || "";
      const body = {
        awbNumber: String(awbNumber || ""),
        status: nextStatus, // "approved" | "rejected"
        approvedBy,
        source: "HUB_APP",
      };

      const resp = await API.Api(urls.RTO_REQUEST_ACTION).patchCustomHeader(body, {
        "Content-Type": "application/json",
      });
      if (!resp?.status) throw new Error(resp?.response?.data?.message || "Update failed");

      setApprovedAwbs((prev) => {
        const copy = new Set(prev);
        const key = String(awbNumber);
        if (nextStatus === "approved") {
          copy.add(key);
        } else {
          copy.delete(key);
        }
        return copy;
      });
      setRejectedAwbs((prev) => {
        const copy = new Set(prev);
        const key = String(awbNumber);
        if (nextStatus === "rejected") {
          copy.add(key);
        } else {
          copy.delete(key);
        }
        return copy;
      });

      message.success(SUCCESS_MESSAGES.STATUS_UPDATED(nextStatus, awbNumber));
      await fetchData();
    } catch (e) {
      console.error(e);
      message.error(ERROR_MESSAGES.STATUS_UPDATE_FAILED(nextStatus, awbNumber));
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "S.No",
        key: "index",
        align: "center",
        width: 70,
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
      },
      {
        title: "Awb Number",
        dataIndex: "awbNumber",
        key: "awbNumber",
        align: "center",
        render: (text) => (
          <span
            style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              const url = `/shipment-tracker?awb=${text}`;
              window.open(url, "_blank");
            }}
          >
            {text}
          </span>
        ),
      },
      {
        title: "CP Name",
        dataIndex: "cpName",
        key: "cpName",
        align: "center",
        render: (text) => <span>{text || "-"}</span>,
      },
      {
        title: "CP Id",
        dataIndex: "cpId",
        key: "cpId",
        align: "center",
        render: (text) => <span>{text || "-"}</span>,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        align: "center",
        render: (text) => {
          const val = String(text || "").toLowerCase();
          const color = val === "approved" ? STATUS_COLORS.APPROVED : val === "rejected" ? STATUS_COLORS.REJECTED : undefined;
          const weight = val === "approved" || val === "rejected" ? 600 : 400;
          const display = toTitleCase(text || "");
          return <span style={{ color, fontWeight: weight }}>{display || "-"}</span>;
        },
      },
      {
        title: "RTO Reason",
        dataIndex: "rtoReason",
        key: "rtoReason",
        align: "center",
        render: (text) => <span>{text || "-"}</span>,
      },
      {
        title: "Remarks",
        dataIndex: "remarks",
        key: "remarks",
        align: "center",
        render: (text) => <span>{text || "-"}</span>,
      },
      {
        title: "Requested Date",
        dataIndex: "requestedDate",
        key: "requestedDate",
        align: "center",
        render: (text) =>
          <span>{text ? dayjs(text, "DD/MM/YYYY HH:mm:ss").format("DD-MM-YYYY | HH:mm:ss") : "-"}</span>,
      },
      {
        title: "Action",
        key: "action",
        align: "center",
        render: (_, record) => {
          const isApproved =
            String(record?.status || "").toLowerCase() === "approved" ||
            approvedAwbs.has(String(record?.awbNumber));
          const isRejected =
            String(record?.status || "").toLowerCase() === "rejected" ||
            rejectedAwbs.has(String(record?.awbNumber));

          const isDisabledApprove = loading || isApproved;
          const isDisabledReject = loading || isRejected || isApproved;

          const approveStyle = approveButtonStyle(isDisabledApprove);
          const rejectStyle = rejectButtonStyle(isDisabledReject);
          return (
            <Space>
              {!isRejected && (
                <Popconfirm
                  title={<span style={{ textTransform: "initial" }}>Are you sure you want to approve this?</span>}
                  onConfirm={() => updateRtoRequestStatus(record?.awbNumber, "approved")}
                  okText="Yes"
                  cancelText="No"
                  disabled={isDisabledApprove}
                  icon={<QuestionCircleOutlined style={{ color: "green" }} />}
                >
                  <Button
                    shape="round"
                    size="small"
                    style={approveStyle}
                    disabled={isDisabledApprove}
                  >
                    Approve
                  </Button>
                </Popconfirm>
              )}
              {!isApproved && (
                <Popconfirm
                  title={<span style={{ textTransform: "initial" }}>Are you sure you want to reject this?</span>}
                  onConfirm={() => updateRtoRequestStatus(record?.awbNumber, "rejected")}
                  okText="Yes"
                  cancelText="No"
                  disabled={isDisabledReject}
                  icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                >
                  <Button
                    shape="round"
                    size="small"
                    style={rejectStyle}
                    disabled={isDisabledReject}
                  >
                    Reject
                  </Button>
                </Popconfirm>
              )}
            </Space>
          );
        },
      },
    ],
    [currentPage, pageSize, loading, approvedAwbs, rejectedAwbs]
  );

  // Reset to page 1 when filters change; do not override server total
  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [awbQuery, cpIdQuery, statusFilter]);

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const clearFilters = () => {
    setAwbQuery("");
    setCpIdQuery("");
    setStatusFilter("requested");
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  return (
    <div className="approvals-rto-request-page" style={{ padding: "16px" }}>
      <Heading title={"RTO Request Approvals"} navigateTo={"/approvals-settings"} />
      <Spin
        spinning={loading}
        indicator={<img src={Smcs_Loader} style={{ height: "100px", width: "100px" }} />}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "nowrap", marginBottom: 12, overflowX: "auto" }}>
          <Input
            placeholder="Search AWB number"
            value={awbQuery}
            onChange={(e) => setAwbQuery(e.target.value)}
            allowClear
            suffix={<img src={search_icon} />}
            style={{ borderRadius: "5px", height: 40, width: 300, flex: "0 0 auto" }}
          />
          <Input
            placeholder="Search CP Id"
            value={cpIdQuery}
            onChange={(e) => setCpIdQuery(e.target.value)}
            allowClear
            suffix={<img src={search_icon} />}
            style={{ borderRadius: "5px", height: 40, width: 300, flex: "0 0 auto" }}
          />
          <Select
            value={statusFilter}
            onChange={(val) => setStatusFilter(val)}
            options={STATUS_OPTIONS}
            style={{ width: 200, height: 40, flex: "0 0 auto" }}
          />
          <Button onClick={clearFilters} style={{ whiteSpace: "nowrap", height: 40 }} disabled={loading}>
            Clear
          </Button>
        </div>

        <TableComponent
          columns={columns}
          dataSource={rows}
          rowKey="awbNumber"
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
        />
      </Spin>
    </div>
  );
};

export default ApprovalsRTORequest;
